import { SearchOutlined } from 'components/icons';
import { ReactNode, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { useSearchKeyDown, useSearchQueryFromUrl } from './searchInput';

export interface GlobalSearchBarHandle {
    /** Focus the input, select what is already there, and open the panel. */
    focus: () => void;
    /**
     * Put the panel away. For whoever decides the box is a button now: a field that unmounts never
     * blurs, so nothing else would close it, and the layout's backdrop would stay over the page.
     */
    close: () => void;
}

export interface GlobalSearchBarProps {
    ref?: Ref<GlobalSearchBarHandle>;
    /** Where the bar sits. Its width is its own: full at narrow width, at most 460/600 from `sm` up. */
    className?: string;
    /** Fires on every open and close, so the layout can dim the page behind the bar. */
    onOpenChange?: (open: boolean) => void;
    /**
     * Fires when a key puts the panel away — Escape, or an Enter that opens the results page. Not on
     * a blur, so a click elsewhere keeps what is typed for the way back, and not on `close`, which
     * also runs when the box turns into a button and has nothing been dismissed.
     */
    onDismiss?: () => void;
    /**
     * Exactly what is in the box, undebounced. The list the panel draws is built from it beside the
     * box rather than inside the panel, so the query is the caller's and not the box's.
     */
    query: string;
    onQueryChange: (query: string) => void;
    /** The panel under the bar, drawn only while it is open. A squeezed bar does not narrow it. */
    panel?: ReactNode;
    /**
     * Key navigation for whatever the panel drew — the input keeps the focus, so the keys land
     * here. Return `true` when the panel consumed the key and the bar should only swallow it.
     * An `Enter` nobody consumed opens the results page for what is typed.
     */
    onArrowDown?: () => boolean;
    onArrowUp?: () => boolean;
    onEnter?: () => boolean;
    /** The panel's listbox and its highlighted option, for the combobox's ARIA wiring. */
    panelId?: string;
    activeOptionId?: string;
    /**
     * Draws the box as a button calling this, rather than as a field with a panel under it.
     *
     * Where there is no room for a list under the box, the surface this opens carries the real
     * field and the box is only the way in. A button and not a field, because a field would have to
     * raise that surface on focus — and then tabbing onto the box would put a modal over the page
     * with no way past it, which is a keyboard trap. The shortcut and {@link focusGlobalSearch}
     * land on the button and raise the same surface, so there is one way in at every width.
     */
    onActivate?: () => void;
}

/**
 * The header's search bar: the input, its key handling and the panel slot underneath it.
 *
 * It owns the focus and whether the panel is up; the query and the panel are both the caller's,
 * because the list the panel draws has to be known where `aria-activedescendant` is rendered —
 * which is the input here. The dimming behind it is the layout's, because the backdrop has to be
 * painted before the chrome to stay under it — see the header.
 *
 * Given `onActivate` it draws a button instead of the field and none of the above applies: the
 * surface that button raises carries the field, and no panel hangs off this one.
 */
export function GlobalSearchBar({
    ref,
    className,
    onOpenChange,
    onDismiss,
    query,
    onQueryChange,
    panel,
    onArrowDown,
    onArrowUp,
    onEnter,
    panelId,
    activeOptionId,
    onActivate,
}: GlobalSearchBarProps) {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Only for the width rule below: on the results page the box carries a query without focus.
    const urlQuery = useSearchQueryFromUrl();

    const [open, setOpen] = useState(false);

    const changeOpen = useCallback(
        (next: boolean) => {
            setOpen(next);
            onOpenChange?.(next);
        },
        [onOpenChange]
    );

    const focus = useCallback(() => {
        if (onActivate !== undefined) {
            // There is no field here to put the caret in — the surface the button raises has it,
            // and focuses it on the way in. Focusing the button first is what the surface then
            // hands the focus back to when it closes.
            buttonRef.current?.focus();
            onActivate();
            return;
        }
        const input = inputRef.current;
        input?.focus();
        input?.select();
    }, [onActivate]);

    const close = useCallback(() => changeOpen(false), [changeOpen]);

    // Closing gives the focus back rather than leaving a focused input under a hidden panel. This
    // is an effect and not part of `close` so that handing `close` to the panel stays a plain call
    // and not a ref read during render.
    useEffect(() => {
        if (!open) inputRef.current?.blur();
    }, [open]);

    useImperativeHandle(ref, () => ({ focus, close }), [focus, close]);

    useGlobalSearchEntry(focus);

    const dismiss = () => {
        close();
        onDismiss?.();
    };
    const handleKeyDown = useSearchKeyDown({ query, close: dismiss, onArrowDown, onArrowUp, onEnter });

    const placeholder = intl.formatMessage({ id: 'global-search.placeholder' });

    if (onActivate !== undefined) {
        return (
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="dialog"
                onClick={(event) => {
                    // Safari leaves a clicked button unfocused, and what holds the focus as the
                    // surface opens is where that surface puts it back on the way out.
                    event.currentTarget.focus();
                    onActivate();
                }}
                className={cn(
                    'bg-paper border-grey-300 focus-visible:border-primary-focus focus-visible:shadow-focus-ring flex h-11 w-full items-center gap-2.5 rounded-[10px] border px-3 text-left outline-none',
                    className
                )}
            >
                <SearchOutlined aria-hidden className="text-primary size-[15px] flex-none" />
                <span className="text-grey-400 min-w-0 flex-1 truncate text-sm">{placeholder}</span>
            </button>
        );
    }

    const hasPanel = open && panel !== undefined;

    return (
        <div
            className={cn(
                'relative w-full transition-[width] sm:w-[460px] sm:max-w-full sm:min-w-0',
                // Wide while it carries a query, which on the results page it does without focus.
                (open || urlQuery !== '') && 'sm:w-[600px]',
                className
            )}
        >
            <div
                className={cn(
                    'bg-paper flex h-11 items-center gap-2.5 rounded-[10px] border px-3 sm:@container sm:h-9',
                    open ? 'border-primary-focus shadow-focus-ring' : 'border-grey-300'
                )}
            >
                <SearchOutlined aria-hidden className="text-primary size-[15px] flex-none" />
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={hasPanel}
                    aria-controls={hasPanel ? panelId : undefined}
                    aria-activedescendant={hasPanel ? activeOptionId : undefined}
                    aria-autocomplete="list"
                    aria-label={placeholder}
                    placeholder={placeholder}
                    autoComplete="off"
                    spellCheck={false}
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                    onFocus={() => changeOpen(true)}
                    onBlur={() => changeOpen(false)}
                    onKeyDown={handleKeyDown}
                    className="text-grey-700 placeholder:text-grey-400 new:placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none md:text-sm"
                />
                {/* The first thing a squeezed bar gives up: under 220px of box there is no room for
                    both it and the placeholder. */}
                <span
                    aria-hidden
                    className="border-primary-subtle bg-primary-surface text-primary text-caption hidden flex-none rounded-[4px] border px-1.5 py-0.5 font-mono leading-none sm:@min-[220px]:inline"
                >
                    {open ? 'Esc' : SHORTCUT_HINT}
                </span>
            </div>
            {hasPanel && (
                // Swallowing the mousedown keeps the focus — and so the panel — while a row is
                // clicked; the blur would otherwise unmount the row before its click landed. The
                // role says what the element is: a positioning wrapper, not a control. The options
                // inside are deliberately not focusable — this is an `aria-activedescendant`
                // combobox, so the input keeps the focus and `useSearchKeyDown` carries every key.
                // From `sm` up it keeps an open bar's full 600px under a squeezed bar, capped at the header
                // row (`cqw` reads the row, its nearest container): a squeezed bar starts at the row's edge.
                <div
                    role="presentation"
                    className="absolute top-[calc(100%+8px)] left-0 z-(--z-dropdown) w-full sm:w-[min(600px,100cqw)]"
                    onMouseDown={(event) => event.preventDefault()}
                >
                    {panel}
                </div>
            )}
        </div>
    );
}

let focusTheSearch: (() => void) | null = null;

/**
 * Makes `focus` the way into search: ⌘K on macOS and Ctrl+K elsewhere, and {@link focusGlobalSearch}.
 *
 * There is one search surface and it lives as long as the chrome does — the header's bar in the
 * classic design, the dialog in the new one — so registering it here is what lets a screen outside
 * the chrome, the 404 page, start a search without a context around the app.
 */
export function useGlobalSearchEntry(focus: () => void): void {
    useEffect(() => {
        focusTheSearch = focus;
        return () => {
            focusTheSearch = null;
        };
    }, [focus]);

    useEffect(() => {
        const handleShortcut = (event: KeyboardEvent) => {
            // Each platform's own modifier only: on macOS Ctrl+K deletes to the end of the line in a text field.
            if ((isMac() ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === 'k') {
                // Claims the browser's own shortcut even from inside another input.
                event.preventDefault();
                focus();
            }
        };
        document.addEventListener('keydown', handleShortcut);
        return () => document.removeEventListener('keydown', handleShortcut);
    }, [focus]);
}

/** Open the chrome's search from outside it. Does nothing where the chrome is absent. */
export function focusGlobalSearch(): void {
    focusTheSearch?.();
}

export function isMac(): boolean {
    if (typeof navigator === 'undefined') return false;
    const userAgentData = (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData;
    return userAgentData?.platform === 'macOS' || /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
}

/** A key cap rather than copy, which is why it is not translated. */
export const SHORTCUT_HINT = isMac() ? '⌘K' : 'Ctrl K';

/** The key cap of the platform's modifier with Enter: what sends a question or saves a section. */
export const MOD_ENTER_HINT = isMac() ? '⌘↵' : 'Ctrl ↵';
